import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { SurfaceSchema, type Surface } from '@ampshift/shared';
import { TextSurfaceView } from './TextSurface';
import { TableSurfaceView } from './TableSurface';
import { ChartSurfaceView } from './ChartSurface';
import { PatientSummarySurfaceView } from './PatientSummarySurface';
import { CalendarSurfaceView } from './CalendarSurface';
import { SlotPickerSurfaceView } from './SlotPickerSurface';
import { MedicationListSurfaceView } from './MedicationListSurface';
import { ClinicalNoteSurfaceView } from './ClinicalNoteSurface';
import { ConfirmationCardSurfaceView } from './ConfirmationCardSurface';
import { CdsAlertsSurfaceView } from './CdsAlertsSurface';
import { FormSurfaceView } from './FormSurface';
import { ClarificationSurfaceView } from './ClarificationSurface';
import { ErrorSurfaceView } from './ErrorSurface';
import { useSurfaceActions } from './context';
import { useT } from '../i18n/useT';

function PatientIndicatorEffect({ surface }: { surface: Extract<Surface, { type: 'patient-indicator' }> }): null {
  const actions = useSurfaceActions();
  // Depend on primitive identity, not the surface object: the renderer re-parses surfaces on every render,
  // so an object dependency re-ran this effect each render and looped ("Maximum update depth exceeded").
  const patientId = surface.patient?.patientId ?? null;
  const encounterId = surface.activeEncounterId ?? null;
  useEffect(() => {
    actions.setPatientContext(surface.patient, surface.activeEncounterId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, encounterId]);
  return null;
}

function UnknownSurface(): JSX.Element {
  const t = useT();
  return <section className="surface surface-unknown muted small">{t('unknownSurface')}</section>;
}

export function SurfaceView({ surface }: { surface: Surface }): JSX.Element | null {
  switch (surface.type) {
    case 'text':
      return <TextSurfaceView surface={surface} />;
    case 'table':
      return <TableSurfaceView surface={surface} />;
    case 'chart':
      return <ChartSurfaceView surface={surface} />;
    case 'patient-summary':
      return <PatientSummarySurfaceView surface={surface} />;
    case 'calendar':
      return <CalendarSurfaceView surface={surface} />;
    case 'slot-picker':
      return <SlotPickerSurfaceView surface={surface} />;
    case 'medication-list':
      return <MedicationListSurfaceView surface={surface} />;
    case 'clinical-note':
      return <ClinicalNoteSurfaceView surface={surface} />;
    case 'confirmation-card':
      return <ConfirmationCardSurfaceView surface={surface} />;
    case 'cds-alerts':
      return <CdsAlertsSurfaceView surface={surface} />;
    case 'form':
      return <FormSurfaceView surface={surface} />;
    case 'patient-indicator':
      return <PatientIndicatorEffect surface={surface} />;
    case 'clarification':
      return <ClarificationSurfaceView surface={surface} />;
    case 'error':
      return <ErrorSurfaceView surface={surface} />;
    default:
      return <UnknownSurface />;
  }
}

/** Renders a list of surfaces; anything that fails schema validation falls back gracefully. */
export function SurfaceList({ surfaces }: { surfaces: ReadonlyArray<unknown> }): JSX.Element | null {
  // Validate once per surfaces array so child components receive stable objects across re-renders.
  const parsed = useMemo(() => surfaces.map((raw) => SurfaceSchema.safeParse(raw)), [surfaces]);
  if (surfaces.length === 0) return null;
  return (
    <div className="surfaces">
      {parsed.map((p, i) => (p.success ? <SurfaceView key={i} surface={p.data} /> : <UnknownSurface key={i} />))}
    </div>
  );
}
